import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Building2,
  Plus,
  Search,
  MapPin,
  Users,
  DollarSign,
  Eye,
  Edit,
  Image as ImageIcon,
  Loader2,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';
import { useApi } from '../hooks/useApi';
import { propertyAPI } from '../services/api';
import AddPropertyForm from '../components/forms/AddPropertyForm';

const Properties = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  const { data: properties, loading, error, refetch } = useApi(() => propertyAPI.getAllEnhanced());

  const filteredProperties = properties ? properties.filter(property =>
    property.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    property.address.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  const handleDeleteProperty = async (propertyId, propertyName) => {
    if (!window.confirm(`Are you sure you want to delete "${propertyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('landlord_token')}`
        }
      });

      if (response.ok) {
        alert('Property deleted successfully');
        refetch(); // Reload the properties list
      } else {
        const errorData = await response.json();
        alert(`Failed to delete property: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      alert('Error deleting property. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-4">Failed to load properties</p>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const PropertyCard = ({ property }) => (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-0">
        {/* Property Image */}
        <div className="relative h-48 bg-gray-200 rounded-t-lg overflow-hidden">
          {property.images && property.images.length > 0 ? (
            <img
              src={property.images[0]}
              alt={property.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('Image failed to load:', property.images[0]);
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="h-12 w-12 text-gray-400" />
            </div>
          )}
          <div className="absolute top-4 right-4">
            <Badge className={`${property.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}>
              {property.status}
            </Badge>
          </div>
        </div>

        {/* Property Details */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{property.name}</h3>
            <div className="flex space-x-1">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setSelectedProperty(property)}
                title="View Details"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => {
                  setEditingProperty(property);
                  setShowEditForm(true);
                }}
                title="Edit Property"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => handleDeleteProperty(property.id, property.name)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Delete Property"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center text-gray-600 mb-3">
            <MapPin className="h-4 w-4 mr-1" />
            <span className="text-sm">{property.address}</span>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center">
              <Users className="h-4 w-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Occupancy</p>
                <p className="font-semibold">{property.occupied_units}/{property.units}</p>
              </div>
            </div>
            <div className="flex items-center">
              <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Monthly Income</p>
                <p className="font-semibold">{formatCurrency(property.monthly_income)}</p>
              </div>
            </div>
          </div>

          {/* Occupancy Bar */}
          <div className="mb-3">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Occupancy Rate</span>
              <span>{Math.round((property.occupied_units / property.units) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                style={{width: `${(property.occupied_units / property.units) * 100}%`}}
              ></div>
            </div>
          </div>

          <div className="text-xs text-gray-500">
            Added {formatDate(property.created_at)}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Properties</h1>
          <p className="text-gray-600 mt-1">Manage your property portfolio</p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setShowAddForm(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Property
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search properties by name or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline">All Properties</Button>
              <Button variant="outline">Active</Button>
              <Button variant="outline">Maintenance</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Properties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProperties.map((property) => (
          <PropertyCard key={property.id} property={property} />
        ))}
      </div>

      {/* Property Details Modal/Sidebar would go here */}
      {selectedProperty && (
        <Card className="fixed inset-y-0 right-0 w-96 shadow-xl z-50 bg-white">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{selectedProperty.name}</CardTitle>
            <Button variant="ghost" onClick={() => setSelectedProperty(null)}>
              ×
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Property Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Address:</span>
                  <span>{selectedProperty.address}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Units:</span>
                  <span>{selectedProperty.units}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Occupied:</span>
                  <span>{selectedProperty.occupied_units}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Property Value:</span>
                  <span>{formatCurrency(selectedProperty.total_value)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Income:</span>
                  <span>{formatCurrency(selectedProperty.monthly_income)}</span>
                </div>
              </div>
            </div>

            {selectedProperty.images && selectedProperty.images.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">Images</h4>
                <div className="grid grid-cols-2 gap-2">
                  {selectedProperty.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`${selectedProperty.name} ${index + 1}`}
                      className="w-full h-20 object-cover rounded"
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2 className="h-5 w-5 mr-2" />
            Portfolio Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{properties?.length || 0}</p>
              <p className="text-sm text-gray-600">Total Properties</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {properties?.reduce((sum, prop) => sum + prop.units, 0) || 0}
              </p>
              <p className="text-sm text-gray-600">Total Units</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {formatCurrency(properties?.reduce((sum, prop) => sum + prop.monthly_income, 0) || 0)}
              </p>
              <p className="text-sm text-gray-600">Monthly Income</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {formatCurrency(properties?.reduce((sum, prop) => sum + prop.total_value, 0) || 0)}
              </p>
              <p className="text-sm text-gray-600">Total Value</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Property Form */}
      {showAddForm && (
        <AddPropertyForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            refetch();
            setShowAddForm(false);
          }}
        />
      )}

      {/* Edit Property Form */}
      {showEditForm && editingProperty && (
        <AddPropertyForm
          initialData={editingProperty}
          onClose={() => {
            setShowEditForm(false);
            setEditingProperty(null);
          }}
          onSuccess={() => {
            refetch();
            setShowEditForm(false);
            setEditingProperty(null);
          }}
        />
      )}
    </div>
  );
};

export default Properties;